/**
 * @file expiring-products.tsx
 * @module app/modules/dashboard/expiring-products
 * @description Displays products approaching expiry for dashboard monitoring and follow-up.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.2.5
 */

import { useEffect, useState } from "react";
import { Card, Col, Form, Modal, Row } from "react-bootstrap";
import Loader from "../../../Global/loader";
import APICallService from "../../../api/apiCallService";
import { inventory } from "../../../api/apiEndPoints";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dashboard,
  Dozen,
  Piece,
} from "../../../utils/constants";
import Method from "../../../utils/methods";
import { useWarehouse } from "../../context/WarehouseContext";
import CrossSvg from "../../../_admin/assets/media/close.png";

type ExpiringRow = {
  variantId?: string;
  productName: string;
  batchId: string;
  procurementDate: string;
  expiryDate: string;
  qty: string;
};

const BULK_VARIANT_CHUNK_SIZE = 300;

const toDateString = (value: any): string => {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return "-";
  }
  return Method.convertDateToDDMMYYYY(parsed.toISOString());
};

const normalizeText = (value: any): string => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }
  return String(value);
};

const formatQuantityTypes = (quantityTypes?: any[]): string => {
  if (!Array.isArray(quantityTypes) || !quantityTypes.length) {
    return "-";
  }
  const formatted = quantityTypes
    .filter((item: any) => (item?.stockCount || 0) > 0)
    .map((item: any) => {
      const count = item.stockCount;
      const type = Number(item.type);
      let label = "Unit";
      if (type === CartonWithDozens || type === CartonWithPieces) {
        label = count === 1 ? "Carton" : "Cartons";
      } else if (type === Dozen) {
        label = count === 1 ? "Dozen" : "Dozens";
      } else if (type === Piece) {
        label = count === 1 ? "Piece" : "Pieces";
      }
      return `${count} | ${label}`;
    });
  return formatted.length ? formatted.join(", ") : "-";
};

const parseInventoryListRecords = (
  records: any[],
  selectedWarehouseId?: string
): ExpiringRow[] => {
  return (records || []).map((row: any) => {
    const variantId = row?.variant?._id || row?._id;
    const title = normalizeText(row?.variant?.title || row?.title);
    const inventoryInfo = row?.variant?.inventoryInfo || [];
    const selectedInventory = selectedWarehouseId
      ? inventoryInfo.find(
        (info: any) =>
          String(info?.warehouseId) === String(selectedWarehouseId)
      ) || inventoryInfo[0]
      : inventoryInfo[0];

    return {
      variantId,
      productName: title,
      batchId: normalizeText(
        selectedInventory?.batchId || selectedInventory?.batch || selectedInventory?.batchNo
      ),
      procurementDate: toDateString(
        selectedInventory?.procurementDate ||
        selectedInventory?.procuredAt ||
        selectedInventory?.createdAt ||
        selectedInventory?._createdAt
      ),
      expiryDate: toDateString(
        selectedInventory?.expiryDate ||
        selectedInventory?.expiry ||
        row?.expiryInfo?.expired?.minDate ||
        row?.expiryInfo?.expiring?.minDate
      ),
      qty: formatQuantityTypes(
        row?.expiryInfo?.expired?.quantityTypes ||
        selectedInventory?.quantityTypes
      ),
    };
  });
};

const pickBestStockRecord = (records: any[], filterValue: string = "1") => {
  if (!records?.length) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let endDate = new Date(today);
  let isExpiredFilter = filterValue === "-1";
  if (!isExpiredFilter) {
    const months = Number(filterValue) || 1;
    endDate.setMonth(endDate.getMonth() + months);
    endDate.setHours(23, 59, 59, 999);
  }

  const inRange = records
    .filter((stock: any) => {
      const d = new Date(stock?.expiry || stock?.expiryDate || 0);
      if (isNaN(d.getTime())) return false;
      return isExpiredFilter ? d < today : d >= today && d <= endDate;
    })
    .sort((a: any, b: any) => {
      const aDate = new Date(a?.expiry || a?.expiryDate || 0).getTime();
      const bDate = new Date(b?.expiry || b?.expiryDate || 0).getTime();
      return isExpiredFilter ? bDate - aDate : aDate - bDate;
    });

  if (inRange.length) {
    return inRange[0];
  }

  const sorted = [...records].sort((a: any, b: any) => {
    const aDate = new Date(a?.expiry || a?.expiryDate || 0).getTime();
    const bDate = new Date(b?.expiry || b?.expiryDate || 0).getTime();
    return isExpiredFilter ? bDate - aDate : aDate - bDate;
  });
  return sorted[0] || null;
};

const chunkArray = <T,>(items: T[], size: number): T[][] => {
  if (!items.length || size <= 0) {
    return [];
  }
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};


type ExpiringProductsModalProps = {
  show: boolean;
  onHide: () => void;
};

const ExpiringProductsModal = ({ show, onHide }: ExpiringProductsModalProps) => {
  const { selectedWarehouse } = useWarehouse();
  const selectedWarehouseId = selectedWarehouse?._id || selectedWarehouse?.value || undefined;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ExpiringRow[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>("1");
  const [totalRecords, setTotalRecords] = useState<number | null>(null);

  const fetchExpiringProducts = async (warehouse?: string, filterValue: string = "1") => {
    setLoading(true);
    setTotalRecords(null);
    const params: any = {
      pageNo: 1,
      limit: 1000,
      sortKey: "title",
      sortOrder: -1,
      state: "1",
      expiry: filterValue,
      viewRemovedOnly: false,
      searchTerm: "",
      lowStock: false,
    };

    if (warehouse) {
      params.warehouse = warehouse;
    }

    const apiService = new APICallService(
      inventory.listInventoryProduct,
      params,
      "",
      "",
      "",
      "",
      Dashboard,
      warehouse || null
    );

    const response: any = await apiService.callAPI();
    const selectedWarehouseId = warehouse || undefined;
    const sourceRecords = response?.records || [];
    const mappedRows = parseInventoryListRecords(sourceRecords, selectedWarehouseId);
    setTotalRecords(response?.total ?? response?.totalRecords ?? response?.count ?? mappedRows.length);

    const rowsMissingDetails = mappedRows.filter(
      (row) =>
        (row.batchId === "-" || row.procurementDate === "-" || row.expiryDate === "-" || row.qty === "-") &&
        row.variantId
    );

    const uniqueVariantIds = Array.from(
      new Set(rowsMissingDetails.map((row) => row.variantId).filter(Boolean))
    ) as string[];

    if (!uniqueVariantIds.length) {
      setRows(mappedRows);
      setLoading(false);
      return;
    }

    const variantChunks = chunkArray(uniqueVariantIds, BULK_VARIANT_CHUNK_SIZE);
    const chunkResponses = await Promise.all(
      variantChunks.map(async (variantsChunk) => {
        const stockApi = new APICallService(
          inventory.variantStockListBulk,
          { variants: variantsChunk },
          "",
          "",
          "",
          "",
          Dashboard,
          warehouse || null
        );
        const stockResponse: any = await stockApi.callAPI();
        return stockResponse?.records || {};
      })
    );

    const stockRecordsMap = chunkResponses.reduce(
      (acc: Record<string, any[]>, chunkMap: Record<string, any[]>) => ({
        ...acc,
        ...chunkMap,
      }),
      {}
    );
    const stockByVariant: Record<string, any> = {};
    uniqueVariantIds.forEach((variantId) => {
      stockByVariant[variantId] = pickBestStockRecord(stockRecordsMap?.[variantId] || [], filterValue);
    });

    const enrichedRows = mappedRows.map((row) => {
      const stock = row.variantId ? stockByVariant[row.variantId] : null;
      if (!stock) {
        return row;
      }
      return {
        ...row,
        batchId:
          row.batchId !== "-"
            ? row.batchId
            : normalizeText(stock?.batch || stock?.batchId || stock?.batchNo),
        procurementDate:
          row.procurementDate !== "-"
            ? row.procurementDate
            : toDateString(stock?._createdAt || stock?.createdAt || stock?.procurementDate),
        expiryDate:
          row.expiryDate !== "-"
            ? row.expiryDate
            : toDateString(stock?.expiry || stock?.expiryDate),
        qty:
          row.qty !== "-"
            ? row.qty
            : formatQuantityTypes(stock?.quantityTypes),
      };
    });

    setRows(enrichedRows);
    setLoading(false);
  };

  useEffect(() => {
    if (!show) {
      setSelectedFilter("1");
      return;
    }
    fetchExpiringProducts(selectedWarehouseId, selectedFilter);
  }, [show, selectedWarehouseId, selectedFilter]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="modal-dialog-centered min-w-lg-1100px min-w-md-900px"
      className="border-r10px"
      contentClassName="p-2"
    >
      <Modal.Header className="border-bottom-0 pb-2">
        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <Modal.Title className="fs-22 fw-bolder mb-0">
              {totalRecords !== null && !loading ? `(${totalRecords}) ` : ""} Products
            </Modal.Title>
            <Form.Select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="fs-15 fw-600 border-r8px py-2 px-3 shadow-none"
              style={{ width: "240px" }}
            >
              <option value="1">Expiring in 1 Month</option>
              <option value="2">Expiring in 2 Months</option>
              <option value="3">Expiring in 3 Months</option>
              <option value="-1">Already Expired</option>
            </Form.Select>
          </div>
          <div className="symbol symbol-md-40px symbol-35px" role="button" onClick={onHide}>
            <img width={40} height={40} src={CrossSvg} alt="close" />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="pt-3 pb-5">
        <Row>
          <Col xs={12}>
            <Card className="border border-r10px">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-rounded table-row-bordered align-middle gs-7 gy-4 mb-0">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom h-70px align-middle">
                        <th className="min-w-250px">Product Name</th>
                        <th className="min-w-150px">Batch ID</th>
                        <th className="min-w-150px">Procurement Date</th>
                        <th className="min-w-150px">Expiry Date</th>
                        <th className="min-w-180px">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading ? (
                        rows.length ? (
                          rows.map((row: ExpiringRow, index: number) => (
                            <tr key={`${row.productName}-${row.batchId}-${index}`}>
                              <td className="fs-15 fw-600">{row.productName}</td>
                              <td className="fs-15 fw-500">{row.batchId}</td>
                              <td className="fs-15 fw-500">{row.procurementDate}</td>
                              <td className="fs-15 fw-500">{row.expiryDate}</td>
                              <td className="fs-15 fw-500">{row.qty}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5}>
                              <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                                No Data found
                              </div>
                            </td>
                          </tr>
                        )
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            <div className="w-100 d-flex justify-content-center">
                              <Loader loading={loading} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ExpiringProductsModal;
